import React, { useEffect, useMemo, useState } from "react";
import { Input, Table, Tabs } from "antd";
import { SearchOutlined, RightOutlined } from "@ant-design/icons";
import styled from "styled-components";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import moment from "moment";
import UseApi from "../../../Reusable/CallApi/UseApi";

const formatDate = (value) => (value ? moment(value).format("Do MMM YYYY") : "-");

const formatStatus = (value) =>
  value
    ? value
        .split("_")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ")
    : "-";

const PageWrapper = styled.div`
  text-align: left;
`;

const Breadcrumb = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 20px;

  .crumb-link {
    cursor: pointer;
    font-size: 16px;
    font-weight: 400;
    color: #636980;
  }

  .crumb-current {
    font-size: 16px;
    font-weight: 400;
    color: #1f243b;
  }

  .anticon {
    font-size: 14px;
    color: #636980;
  }
`;

const StatsRow = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 10px;
  padding: 20px 0;
`;

const StatCard = styled.div`
  flex: 1;
  padding: 20px;
  border-radius: 8px;
  text-align: center;
  background-color: #f4f4f4;
`;

const StatNumber = styled.p`
  font-size: 28px;
  font-weight: 700;
  margin: 0;
`;

const StatLabel = styled.p`
  font-size: 16px;
  font-weight: 400;
  margin: 0;
`;

const SearchInput = styled(Input)`
  color: #636980;
  border: 1px solid #e2e2e8;
  width: 210px;
  border-radius: 24px;
  font-size: 14px;
  font-weight: 400;
  margin-bottom: 16px;
`;

const StyledTable = styled(Table)`
  border: 1px solid #e2e2e8;
  border-radius: 8px;

  .ant-table-thead > tr > th {
    font-size: 16px;
    font-weight: 500;
    color: #1f243b;
  }

  .ant-table-tbody > tr > td {
    font-size: 14px;
    font-weight: 400;
    color: #636980;
  }

  .table-row-white {
    background-color: #ffffff;
  }

  .table-row-gray {
    background-color: #f5f5f5;
  }
`;

const StyledTabs = styled(Tabs)`
  .ant-tabs-tab-active .ant-tabs-tab-btn {
    color: black !important;
  }

  .ant-tabs-ink-bar {
    background-color: black !important;
  }

  .ant-tabs-tab {
    color: #636980 !important;
    font-weight: 500;
    font-size: 16px;
  }
`;

const peopleColumns = [
  { title: "Employee ID", dataIndex: "employeeId", key: "employeeId" },
  { title: "Employee Name", dataIndex: "employeeName", key: "employeeName" },
  { title: "Status", dataIndex: "statusLabel", key: "statusLabel" },
];

const detailColumns = [
  { title: "Employee ID", dataIndex: "employeeId", key: "employeeId" },
  { title: "Employee Name", dataIndex: "employeeName", key: "employeeName" },
  { title: "Status", dataIndex: "statusLabel", key: "statusLabel" },
  { title: "Progress", dataIndex: "progress", key: "progress" },
  { title: "Enrolled At", dataIndex: "enrolledAtLabel", key: "enrolledAtLabel" },
  { title: "Completed On", dataIndex: "completedOnLabel", key: "completedOnLabel" },
];

const activityColumns = [
  { title: "Activity Type", dataIndex: "actionType", key: "actionType" },
  { title: "Date & Time", dataIndex: "actionDate", key: "actionDate" },
  { title: "Detail", dataIndex: "actionLog", key: "actionLog" },
];

function Designtask() {
  const navigate = useNavigate();
  const { taskId } = useParams();
  const location = useLocation();
  const clientId = useSelector((state) => state.authTracking.userInfo.clientId);

  const taskApi = UseApi();
  const detailApi = UseApi();
  const activityApi = UseApi();

  const [task, setTask] = useState(location.state?.task || null);
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    if (!clientId || task) return;
    taskApi
      .request({ endPoint: `/report/gettaskreport/${clientId}`, method: "get" })
      .then((result) => {
        const match = (result?.response || []).find(
          (item) => String(item.taskId) === String(taskId)
        );
        setTask(match || null);
      })
      .catch(() => setTask(null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId, taskId]);

  useEffect(() => {
    if (!clientId) return;
    // UseApi's request() rethrows on failure - both were uncaught here,
    // genuine unhandled promise rejections (CRA's dev error overlay
    // whenever either endpoint fails).
    detailApi.request({ endPoint: `/report/gettaskdetailreport/${clientId}`, method: "get" }).catch(() => {});
    activityApi.request({ endPoint: `/report/getactivitylog/${clientId}`, method: "get" }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId]);

  const detailData = (detailApi.data?.response || [])
    .filter((item) => String(item.taskId) === String(taskId))
    .map((item) => ({
      ...item,
      statusLabel: formatStatus(item.status),
      enrolledAtLabel: formatDate(item.enrolledAt),
      completedOnLabel: formatDate(item.completedOn),
    }));
  const peopleData = detailData;
  const activityData = activityApi.data?.response || [];

  const cardData = useMemo(() => {
    const enrolled = task?.peopleEnrolled ?? 0;
    const completed = task?.peopleCompleted ?? 0;
    const inProgress = task?.peopleInProgress ?? 0;
    const notPassed = task?.peopleNotPassed ?? 0;
    const notAttended = Math.max(enrolled - completed - inProgress - notPassed, 0);
    return [
      { id: 1, title: enrolled, description: "People" },
      { id: 2, title: completed, description: "Completed" },
      { id: 3, title: inProgress, description: "In Progress" },
      { id: 4, title: notPassed, description: "Not Passed" },
      { id: 5, title: notAttended, description: "Not Attended" },
    ];
  }, [task]);

  const tableConfig = {
    people: { columns: peopleColumns, data: peopleData, loading: detailApi.loading },
    details: { columns: detailColumns, data: detailData, loading: detailApi.loading },
    activity: { columns: activityColumns, data: activityData, loading: activityApi.loading },
  };

  const buildTabContent = (tabKey) => {
    const { columns, data, loading } = tableConfig[tabKey];
    const filteredData = data.filter((row) =>
      Object.values(row).some((value) =>
        String(value).toLowerCase().includes(searchTerm.toLowerCase())
      )
    );
    return (
      <>
        <SearchInput
          placeholder="Search"
          prefix={<SearchOutlined style={{ color: "#636980" }} />}
          value={searchTerm}
          onChange={(event) => setSearchTerm(event.target.value)}
        />
        <StyledTable
          columns={columns}
          dataSource={filteredData}
          rowKey={(record, index) => index}
          loading={loading}
          rowClassName={(record, index) =>
            index % 2 === 0 ? "table-row-white" : "table-row-gray"
          }
          pagination={false}
          scroll={{ x: true }}
        />
      </>
    );
  };

  const items = [
    { key: "people", label: "People", children: buildTabContent("people") },
    { key: "details", label: "Details", children: buildTabContent("details") },
    { key: "activity", label: "Activity Log", children: buildTabContent("activity") },
  ];

  return (
    <PageWrapper>
      <Breadcrumb>
        <span className="crumb-link" onClick={() => navigate("/coursetask")}>
          Course Task Report
        </span>
        <RightOutlined />
        <span className="crumb-current">{task?.taskName}</span>
      </Breadcrumb>

      <StatsRow>
        {cardData.map((card) => (
          <StatCard key={card.id}>
            <StatNumber>{card.title}</StatNumber>
            <StatLabel>{card.description}</StatLabel>
          </StatCard>
        ))}
      </StatsRow>

      <StyledTabs
        tabBarStyle={{ width: "255px" }}
        defaultActiveKey="people"
        items={items}
        onChange={() => setSearchTerm("")}
      />
    </PageWrapper>
  );
}

export default Designtask;
